import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { ContentPiece } from "../../../shared/types";

// ── Mutations ──

export function usePublishRun(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (target?: { linkedinConnectionId?: string; linkedinResourceId?: string; blueskyConnectionId?: string; blueskyResourceId?: string; idempotencyKey?: string }) =>
      api.post<{ data: ContentPiece[] }>(
        `/api/publishing/run/${runId}/publish`,
        target ? {
          linkedin_connection_id: target.linkedinConnectionId,
          linkedin_resource_id: target.linkedinResourceId,
          bluesky_connection_id: target.blueskyConnectionId,
          bluesky_resource_id: target.blueskyResourceId,
        } : undefined,
        target?.idempotencyKey ? { "Idempotency-Key": target.idempotencyKey } : undefined,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content-runs", runId, "pieces"] });
      qc.invalidateQueries({ queryKey: ["content-runs", runId] });
      qc.invalidateQueries({ queryKey: ["content-runs", runId, "publish-status"] });
      // Publishing sets pieces to 'published' (drives the "Ready" run label) — refresh
      // the workspace KPI + content summary too.
      qc.invalidateQueries({ queryKey: ["brand-stats"] });
      qc.invalidateQueries({ queryKey: ["content-summary"] });
    },
  });
}

export function useSchedulePublish(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { publish_at: string }) =>
      api.post<{ data: ContentPiece[] }>(
        `/api/publishing/run/${runId}/schedule`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content-runs", runId, "pieces"] });
      qc.invalidateQueries({ queryKey: ["content-runs", runId] });
      // Publishing sets pieces to 'published' (drives the "Ready" run label) — refresh
      // the workspace KPI + content summary too.
      qc.invalidateQueries({ queryKey: ["brand-stats"] });
      qc.invalidateQueries({ queryKey: ["content-summary"] });
    },
  });
}

export function useRetryPublish() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: string | { pieceId: string; linkedinConnectionId?: string; linkedinResourceId?: string; blueskyConnectionId?: string; blueskyResourceId?: string }) => {
      const target = typeof input === "string" ? { pieceId: input } : input;
      return api.post<{ data: ContentPiece }>(
        `/api/publishing/pieces/${target.pieceId}/retry`,
        {
          linkedin_connection_id: target.linkedinConnectionId,
          linkedin_resource_id: target.linkedinResourceId,
          bluesky_connection_id: target.blueskyConnectionId,
          bluesky_resource_id: target.blueskyResourceId,
        },
        target.blueskyConnectionId ? { "Idempotency-Key": globalThis.crypto.randomUUID() } : undefined,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content-runs"] });
      qc.invalidateQueries({ queryKey: ["brand-stats"] });
      qc.invalidateQueries({ queryKey: ["content-summary"] });
    },
  });
}

export function useReconcileBlueskyPublish() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { pieceId: string; connectionId: string; resourceId: string }) =>
      api.post<{ data: ContentPiece }>(
        `/api/publishing/bluesky/pieces/${input.pieceId}/reconcile`,
        { connection_id: input.connectionId, resource_id: input.resourceId },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content-runs"] });
      qc.invalidateQueries({ queryKey: ["brand-stats"] });
      qc.invalidateQueries({ queryKey: ["content-summary"] });
    },
  });
}

// ── Queries ──

export function usePublishStatus(runId: string | undefined) {
  return useQuery({
    queryKey: ["content-runs", runId, "publish-status"],
    queryFn: () =>
      api.get<{ data: ContentPiece[] }>(
        `/api/publishing/run/${runId}/status`,
      ),
    enabled: !!runId,
    refetchInterval: (query) => {
      const pieces = query.state.data?.data;
      if (!Array.isArray(pieces)) return false;
      const isPublishing = pieces.some(
        (p) => p.publish_status === "publishing",
      );
      return isPublishing ? 5_000 : false;
    },
  });
}
